// The generated typed fetch client shell (API-40, AP-D9). Surfaces and the Expo
// apps NEVER hand-roll requests (CB-D5) — they call this. It is typed against the
// generated OpenAPI `paths` and centralizes: X-Request-Id (API-8), Accept-Language
// (API-7), Idempotency-Key headers (AP-D7), ETag revalidation (API-32),
// single-flight auth refresh (AP-D4), and the retry policy (API-40). fetch-only —
// runs unchanged on Cloudflare Workers and Expo/React Native; no Node APIs.

import type { paths } from '../generated/types';
import { generateRequestId } from './request-id';
import { createEtagCache, type EtagCache } from './etag-cache';
import {
  defaultRetryPolicy,
  isRetryableMethod,
  isRetryableStatus,
  backoffDelayMs,
  retryAfterMs,
  sleep,
  type RetryPolicy,
} from './retry-policy';
import { createSingleFlightRefresh, type RefreshFn } from './refresh-single-flight';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

type MediaJson<T> = T extends { content: { 'application/json': infer J } } ? J : never;

type OkResponse<R> = R extends { 200: infer X }
  ? MediaJson<X>
  : R extends { 201: infer X }
    ? MediaJson<X>
    : R extends { 202: infer X }
      ? MediaJson<X>
      : R extends { 204: unknown }
        ? undefined
        : unknown;

type ResponseOf<O> = O extends { responses: infer R } ? OkResponse<R> : unknown;

type JsonBodyOf<O> = O extends { requestBody?: { content: { 'application/json': infer B } } }
  ? B
  : never;

type ParamsOf<O> = O extends { parameters: infer P } ? P : Record<string, never>;
type PathParamsOf<O> =
  ParamsOf<O> extends { path?: infer P }
    ? P extends undefined
      ? Record<string, never>
      : P
    : Record<string, never>;
type QueryParamsOf<O> =
  ParamsOf<O> extends { query?: infer Q }
    ? Q extends undefined
      ? Record<string, never>
      : Q
    : Record<string, never>;

// Present the awkward `never`/`Record<never>` derived params as clean optionals.
type Maybe<T> = [keyof T] extends [never] ? Record<string, never> : T;

export interface RequestOptions<O> {
  params?: {
    path?: Maybe<PathParamsOf<O>>;
    query?: Maybe<QueryParamsOf<O>>;
  };
  body?: JsonBodyOf<O> | FormData;
  /** Mandatory on money/inventory-mutating POSTs (AP-D7); enables safe retries. */
  idempotencyKey?: string;
  /** `?lang=` + Accept-Language for localized reads (API-7). */
  lang?: 'bn' | 'en';
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ClientOptions {
  /** Per-environment API base, including the `/v1` prefix (AP-D3). */
  baseUrl: string;
  /** Access token provider (held in memory / secure storage, never localStorage — AP-D4). */
  getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /** Performs a refresh and returns a fresh access token, or null if it failed. */
  refreshAccessToken?: RefreshFn;
  /** Injectable for tests / non-global fetch. Defaults to the platform `fetch`. */
  fetch?: typeof fetch;
  retryPolicy?: RetryPolicy;
  defaultLang?: 'bn' | 'en';
  etagCache?: EtagCache;
  /**
   * Fetch credentials mode. Web surfaces set `'include'` so the HttpOnly refresh
   * cookie (AP-D4) rides same-site cross-origin calls to the api Worker; native
   * clients (no cookies) leave it unset. Defaults to the platform default.
   */
  credentials?: RequestCredentials;
}

// RFC 9457 problem carrier (§6.5). Callers branch on `code`.
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly problem?: unknown;
  readonly requestId?: string;

  constructor(status: number, problem: unknown, requestId?: string) {
    const code =
      problem && typeof problem === 'object' && 'code' in problem
        ? String((problem as { code: unknown }).code)
        : undefined;
    const detail =
      problem && typeof problem === 'object' && 'detail' in problem
        ? String((problem as { detail: unknown }).detail)
        : `HTTP ${status}`;
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.problem = problem;
    this.requestId = requestId;
  }
}

function buildUrl(
  baseUrl: string,
  path: string,
  pathParams: Record<string, unknown> | undefined,
  query: Record<string, unknown> | undefined,
  lang: string | undefined,
): string {
  let resolved = path;
  if (pathParams) {
    for (const [key, value] of Object.entries(pathParams)) {
      resolved = resolved.replace(`{${key}}`, encodeURIComponent(String(value)));
    }
  }
  const url = new URL(baseUrl.replace(/\/$/, '') + resolved);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) url.searchParams.append(key, String(v));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  if (lang && !url.searchParams.has('lang')) url.searchParams.set('lang', lang);
  return url.toString();
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('json')) {
    return response.json().catch(() => undefined);
  }
  return response.text().catch(() => undefined);
}

export interface Client {
  request<P extends keyof paths, M extends HttpMethod & keyof paths[P]>(
    method: M,
    path: P,
    options?: RequestOptions<paths[P][M]>,
  ): Promise<ResponseOf<paths[P][M]>>;
  GET<P extends keyof paths, M extends 'get' & keyof paths[P]>(
    path: P,
    options?: RequestOptions<paths[P][M]>,
  ): Promise<ResponseOf<paths[P][M]>>;
  POST<P extends keyof paths, M extends 'post' & keyof paths[P]>(
    path: P,
    options?: RequestOptions<paths[P][M]>,
  ): Promise<ResponseOf<paths[P][M]>>;
  PUT<P extends keyof paths, M extends 'put' & keyof paths[P]>(
    path: P,
    options?: RequestOptions<paths[P][M]>,
  ): Promise<ResponseOf<paths[P][M]>>;
  PATCH<P extends keyof paths, M extends 'patch' & keyof paths[P]>(
    path: P,
    options?: RequestOptions<paths[P][M]>,
  ): Promise<ResponseOf<paths[P][M]>>;
  DELETE<P extends keyof paths, M extends 'delete' & keyof paths[P]>(
    path: P,
    options?: RequestOptions<paths[P][M]>,
  ): Promise<ResponseOf<paths[P][M]>>;
}

export function createClient(options: ClientOptions): Client {
  const doFetch = options.fetch ?? globalThis.fetch;
  const retryPolicy = options.retryPolicy ?? defaultRetryPolicy;
  const etagCache = options.etagCache ?? createEtagCache();
  const refresher = options.refreshAccessToken
    ? createSingleFlightRefresh(options.refreshAccessToken)
    : undefined;

  async function authHeader(): Promise<string | undefined> {
    const token = await options.getAccessToken?.();
    return token ? `Bearer ${token}` : undefined;
  }

  async function send(
    method: string,
    path: string,
    opts: RequestOptions<unknown>,
  ): Promise<unknown> {
    const upperMethod = method.toUpperCase();
    const lang = opts.lang ?? options.defaultLang;
    const url = buildUrl(
      options.baseUrl,
      path,
      opts.params?.path as Record<string, unknown> | undefined,
      opts.params?.query as Record<string, unknown> | undefined,
      lang,
    );

    const hasIdempotencyKey = Boolean(opts.idempotencyKey);
    const cacheable = upperMethod === 'GET';
    const cached = cacheable ? etagCache.get(url) : undefined;

    let attempt = 0;
    let refreshed = false;

    for (;;) {
      const headers = new Headers(opts.headers);
      headers.set('X-Request-Id', headers.get('X-Request-Id') ?? generateRequestId());
      if (lang) headers.set('Accept-Language', lang);
      if (opts.idempotencyKey) headers.set('Idempotency-Key', opts.idempotencyKey);
      if (cached) headers.set('If-None-Match', cached.etag);

      const auth = await authHeader();
      if (auth) headers.set('Authorization', auth);

      let bodyInit: BodyInit | undefined;
      if (opts.body !== undefined) {
        if (typeof FormData !== 'undefined' && opts.body instanceof FormData) {
          // Multipart upload (e.g. a payment-proof screenshot): pass the FormData
          // through and let the runtime set the `multipart/form-data` Content-Type
          // with its boundary — never override it here.
          bodyInit = opts.body;
        } else {
          headers.set('Content-Type', 'application/json');
          bodyInit = JSON.stringify(opts.body);
        }
      }

      let response: Response;
      try {
        response = await doFetch(url, {
          method: upperMethod,
          headers,
          body: bodyInit,
          signal: opts.signal,
          ...(options.credentials ? { credentials: options.credentials } : {}),
        });
      } catch (err) {
        if (isRetryableMethod(upperMethod, hasIdempotencyKey) && attempt < retryPolicy.retries) {
          await sleep(backoffDelayMs(attempt, retryPolicy));
          attempt += 1;
          continue;
        }
        throw err;
      }

      // Conditional-GET hit: replay the cached body (headers-only cost, API-32).
      if (response.status === 304 && cached) {
        return cached.body;
      }

      // Expired access token: refresh once (single-flight) and retry.
      if (response.status === 401 && refresher && !refreshed) {
        refreshed = true;
        const token = await refresher.run();
        if (token) continue;
      }

      if (
        isRetryableStatus(response.status) &&
        isRetryableMethod(upperMethod, hasIdempotencyKey) &&
        attempt < retryPolicy.retries
      ) {
        const wait = retryAfterMs(response) ?? backoffDelayMs(attempt, retryPolicy);
        await sleep(wait);
        attempt += 1;
        continue;
      }

      const body = await readBody(response);

      if (!response.ok) {
        throw new ApiError(
          response.status,
          body,
          response.headers.get('X-Request-Id') ?? undefined,
        );
      }

      const etag = response.headers.get('ETag');
      if (cacheable && etag) etagCache.set(url, { etag, body });

      return body;
    }
  }

  const request = ((method: string, path: string, opts: RequestOptions<unknown> = {}) =>
    send(method, path, opts)) as Client['request'];

  return {
    request,
    GET: (path, opts) => request('get' as never, path, opts as never),
    POST: (path, opts) => request('post' as never, path, opts as never),
    PUT: (path, opts) => request('put' as never, path, opts as never),
    PATCH: (path, opts) => request('patch' as never, path, opts as never),
    DELETE: (path, opts) => request('delete' as never, path, opts as never),
  };
}
